// src/utils/api.js
const API_URL = process.env.REACT_APP_API_URL || 'https://drivesoft-v2-anhu4.ondigitalocean.app/';

// Fonction générique pour les appels API
const fetchWithAuth = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');

  if (!token) {
    console.error(`API: Tentative d'appel à ${endpoint} sans token d'authentification`);
    throw new Error('Pas de token d\'authentification. Veuillez vous reconnecter.');
  }

  // Simplification pour utiliser exactement le format attendu par le backend
  const headers = {
    'Content-Type': 'application/json',
    // Le backend utilise un middleware authenticateToken qui attend ce format
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  console.log(`API: Appel à ${endpoint}`);

  try {
    // Utiliser URL simple sans paramètre token
    const url = `${API_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`API: Réponse de ${endpoint}`, { status: response.status, ok: response.ok });

    if (!response.ok) {
      let errorMessage = `Erreur ${response.status}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || `Erreur ${response.status}`;
        console.error(`API: Erreur ${endpoint}`, errorData);
      }
      catch (parseError) {
        console.error('API: Erreur de parsing de la réponse d\'erreur', parseError);
      }

      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log(`API: Données reçues de ${endpoint}`, data);
      return data;
    }

    const text = await response.text();
    console.log(`API: Texte reçu de ${endpoint}`, text);
    return { success: true, text };
  }
  catch (error) {
    console.error(`API: Erreur lors de l'appel à ${endpoint}`, error);
    throw error;
  }
};

// Authentification - Utilise la route d'authentification admin
export const login = async (email, password) => {
  console.log('API: Tentative de connexion', { email });

  try {
    const response = await fetch(`${API_URL}/admin-auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    console.log('API: Réponse de login', { status: response.status, ok: response.ok });

    if (!response.ok) {
      let errorMessage = `Erreur de connexion (${response.status})`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
        console.error('API: Erreur de connexion', errorData);
      }
      catch (parseError) {
        console.error('API: Erreur de parsing de la réponse d\'erreur de connexion', parseError);
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log(
      'API: Connexion réussie, données reçues:',
      { token: data.token ? 'Présent' : 'Absent', userId: data.userId }
    );

    return data;
  }
  catch (error) {
    console.error('API: Erreur lors de la connexion', error);
    throw error;
  }
};

// Tenants
export const getAllTenants = () => fetchWithAuth('/admin/tenants');

export const getTenantById = (id) => fetchWithAuth(`/admin/tenants/${id}`);

export const createTenant = (tenantData) => {
  // Mappage des champs pour correspondre au format attendu par l'API
  const mappedData = {
    company_name: tenantData.companyName,
    subdomain: tenantData.subdomain,
    plan_id: tenantData.planId,
    contact_email: tenantData.contactEmail,
    contact_name: tenantData.contactName,
    contact_phone: tenantData.contactPhone,
    status: tenantData.status,
    bucket_name: tenantData.bucketName,
    logo_url: tenantData.logoUrl,
    signature_image: tenantData.signatureImage,
    signature_name: tenantData.signatureName,
    primary_color: tenantData.primaryColor,
    secondary_color: tenantData.secondaryColor,
    max_users: tenantData.maxUsers,
    trial_ends_at: tenantData.trialEndsAt,
    db_connection_string: tenantData.dbConnectionString,
  };

  return fetchWithAuth('/admin/tenants', {
    method: 'POST',
    body: JSON.stringify(mappedData),
  });
};

export const updateTenant = (id, updateData) => {
  // Mappage des champs pour correspondre au format attendu par l'API
  const mappedData = {
    company_name: updateData.companyName,
    plan_id: updateData.planId,
    contact_email: updateData.contactEmail,
    contact_name: updateData.contactName,
    contact_phone: updateData.contactPhone,
    status: updateData.status,
    bucket_name: updateData.bucketName,
    logo_url: updateData.logoUrl,
    signature_image: updateData.signatureImage,
    signature_name: updateData.signatureName,
    primary_color: updateData.primaryColor,
    secondary_color: updateData.secondaryColor,
    max_users: updateData.maxUsers,
    trial_ends_at: updateData.trialEndsAt,
    db_connection_string: updateData.dbConnectionString,
  };

  return fetchWithAuth(`/admin/tenants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(mappedData),
  });
};

export const deleteTenant = (id) => fetchWithAuth(`/admin/tenants/${id}`, {
  method: 'DELETE',
});

// Plans
export const getAllPlans = () => fetchWithAuth('/admin/plans');

export const getPlanById = (id) => fetchWithAuth(`/admin/plans/${id}`);

export const createPlan = (planData) => fetchWithAuth('/admin/plans', {
  method: 'POST',
  body: JSON.stringify(planData),
});

export const updatePlan = (id, updateData) => fetchWithAuth(`/admin/plans/${id}`, {
  method: 'PUT',
  body: JSON.stringify(updateData),
});

export const deletePlan = (id) => fetchWithAuth(`/admin/plans/${id}`, {
  method: 'DELETE',
});

// Features
export const getTenantFeatures = (tenantId) => fetchWithAuth(`/admin/tenants/${tenantId}/features`);

export const updateFeatureFlag = (tenantId, featureKey, isEnabled) => fetchWithAuth(`/admin/tenants/${tenantId}/features/${featureKey}`, {
  method: 'PUT',
  body: JSON.stringify({ isEnabled }),
});

export const resetTenantFeaturesToPlan = (tenantId) => fetchWithAuth(`/admin/tenants/${tenantId}/features/reset`, {
  method: 'POST',
});

export const getAllFeatureDefinitions = () => fetchWithAuth('/admin/features/definitions');

export const getFeatureDefinitionByKey = (key) => fetchWithAuth(`/admin/features/definitions/${key}`);

export const createFeatureDefinition = (featureData) => fetchWithAuth('/admin/features/definitions', {
  method: 'POST',
  body: JSON.stringify(featureData),
});

export const updateFeatureDefinition = (key, updateData) => fetchWithAuth(`/admin/features/definitions/${key}`, {
  method: 'PUT',
  body: JSON.stringify(updateData),
});

export const deleteFeatureDefinition = (key) => fetchWithAuth(`/admin/features/definitions/${key}`, {
  method: 'DELETE',
});

export default {
  login,
  getAllTenants,
  getTenantById,
  createTenant,
  updateTenant,
  deleteTenant,
  getAllPlans,
  getPlanById,
  createPlan,
  updatePlan,
  deletePlan,
  getTenantFeatures,
  updateFeatureFlag,
  resetTenantFeaturesToPlan,
  getAllFeatureDefinitions,
  getFeatureDefinitionByKey,
  createFeatureDefinition,
  updateFeatureDefinition,
  deleteFeatureDefinition,
};
